import React from 'react';
import { Box, Stack, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssessmentIcon from '@mui/icons-material/Assessment';

const SortableQuestionItem = ({ question, index, onEdit, onDelete, onViewReport, isCurrent }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: question.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 0: return 'Çoktan Seçmeli';
      case 1: return 'Değerlendirme (1-5)';
      case 2: return 'Sayı';
      default: return 'Bilinmeyen';
    }
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 2,
        mb: 1.5,
        border: '1px solid #e6e6e6',
        borderRadius: '8px',
        backgroundColor: isCurrent ? '#E7EDF4' : '#ffffff',
        borderLeft: isCurrent ? '4px solid #0E4D94' : '1px solid #e6e6e6',
        boxShadow: 'none',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
          borderColor: isCurrent ? '#0E4D94' : '#CFDBEA',
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            pt: 0.5,
            color: '#666666',
            '&:hover': {
              color: '#0E4D94',
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#444444',
                flex: '0 0 auto',
              }}
            >
              {index + 1}. {question.questionText}
            </Typography>
            <Chip
              label={getQuestionTypeLabel(question.questionType)}
              size="small"
              sx={{
                height: 24,
                fontSize: '11px',
                fontWeight: 500,
                backgroundColor: '#E7EDF4',
                color: '#0E4D94',
                border: 'none',
              }}
            />
            {question.isRequired && (
              <Chip
                label="Zorunlu"
                size="small"
                sx={{
                  height: 24,
                  fontSize: '11px',
                  fontWeight: 500,
                  backgroundColor: '#FFEEF3',
                  color: '#F8285A',
                  border: 'none',
                }}
              />
            )}
            {isCurrent && (
              <Chip
                label="Aktif"
                size="small"
                sx={{
                  height: 24,
                  fontSize: '11px',
                  fontWeight: 500,
                  backgroundColor: '#0E4D94',
                  color: '#ffffff',
                  border: 'none',
                }}
              />
            )}
          </Stack>
          {question.questionType === 0 && question.options && question.options.length > 0 && (
            <Box
              sx={{
                mt: 1,
                pl: 2,
                borderLeft: '2px solid #e6e6e6',
              }}
            >
              <Stack spacing={0.75}>
              {question.options.map((opt, optIdx) => (
                  <Typography
                    key={optIdx}
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      color: '#666666',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: '#666666',
                        mr: 1,
                      }}
                    >
                      {String.fromCharCode(65 + optIdx)}
                    </Box>
                    {opt}
                </Typography>
              ))}
            </Stack>
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Tooltip title="Rapor">
            <IconButton
              size="small"
              onClick={() => onViewReport(question.id)}
              sx={{
                backgroundColor: '#E7EDF4',
                color: '#0E4D94',
                width: 28,
                height: 28,
                '&:hover': {
                  backgroundColor: '#CFDBEA',
                },
                '& svg': {
                  fontSize: '16px',
                },
              }}
            >
              <AssessmentIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Düzenle">
            <IconButton
              size="small"
              onClick={() => onEdit(question)}
              sx={{
                backgroundColor: '#E7EDF4',
                color: '#0E4D94',
                width: 28,
                height: 28,
                '&:hover': {
                  backgroundColor: '#CFDBEA',
                },
                '& svg': {
                  fontSize: '16px',
                },
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sil">
            <IconButton
              size="small"
              onClick={() => onDelete(question)}
              sx={{
                backgroundColor: '#FFEEF3',
                color: '#F8285A',
                width: 28,
                height: 28,
                '&:hover': {
                  backgroundColor: '#FFD6E0',
                },
                '& svg': {
                  fontSize: '16px',
                },
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SortableQuestionItem;

